import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import OneOmicsLogo from "./OneOmicsLogo";

export default function AdminPanel({ onBack }) {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab]     = useState("overview");
  const [users, setUsers]             = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUser, setNewUser]         = useState({ username: "", password: "", role: "viewer" });
  const [feedback, setFeedback]       = useState(null);
  const [stats, setStats]             = useState({ reports: 0, uploads: 0, users: 0, pending: 0 });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    if (activeTab === "users")   fetchUsers();
    if (activeTab === "pending") fetchPending();
  }, [activeTab]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchStats = async () => {
  try {
    const res  = await authFetch("/api/admin/stats");
    const data = await res.json();
    if (res.ok) setStats(data);
  } catch {}
};

const fetchUsers = async () => {
  setLoadingUsers(true);
  try {
    const res  = await authFetch("/api/admin/users?status=approved");
    const data = await res.json();
    if (res.ok) setUsers(data.users || []);
  } catch {}
  setLoadingUsers(false);
};

const fetchPending = async () => {
  setLoadingUsers(true);
  try {
    const res  = await authFetch("/api/admin/users?status=pending");
    const data = await res.json();
    if (res.ok) setPendingUsers(data.users || []);
  } catch {}
  setLoadingUsers(false);
};

const handleApprove = async (userId) => {
  try {
    const res = await authFetch(`/api/admin/users/${userId}/approve`, { method: "PATCH" });
    if (res.ok) {
      setPendingUsers(prev => prev.filter(u => u._id !== userId));
      setFeedback({ type: "success", msg: "User approved successfully." });
      fetchStats();
    }
  } catch {}
  setTimeout(() => setFeedback(null), 3000);
};

const handleReject = async (userId) => {
  if (!window.confirm("Reject this user's request?")) return;
  try {
    const res = await authFetch(`/api/admin/users/${userId}/reject`, { method: "PATCH" });
    if (res.ok) {
      setPendingUsers(prev => prev.filter(u => u._id !== userId));
      setFeedback({ type: "success", msg: "User rejected." });
      fetchStats();
    }
  } catch {}
  setTimeout(() => setFeedback(null), 3000);
};

const handleCreateUser = async (e) => {
  e.preventDefault();
  if (!newUser.username || !newUser.password) {
    setFeedback({ type: "error", msg: "Username and password are required." }); return;
  }
  try {
    const res  = await authFetch("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(newUser),
    });
    const data = await res.json();
    if (res.ok) {
      setFeedback({ type: "success", msg: "User created successfully." });
      setNewUser({ username: "", password: "", role: "viewer" });
      fetchUsers(); fetchStats();
    } else {
      setFeedback({ type: "error", msg: data.message || "Failed to create user." });
    }
  } catch {
    setFeedback({ type: "error", msg: "Server error." });
  }
  setTimeout(() => setFeedback(null), 3000);
};

const handleDeleteUser = async (userId) => {
  if (!window.confirm("Delete this user?")) return;
  try {
    const res = await authFetch(`/api/admin/users/${userId}`, { method: "DELETE" });
    if (res.ok) {
      setUsers(prev => prev.filter(u => u._id !== userId));
      setFeedback({ type: "success", msg: "User deleted." });
      fetchStats();
    }
  } catch {}
  setTimeout(() => setFeedback(null), 3000);
};

  const handleLogout = () => { logout(); navigate("/login"); };

  const tabs = [
    { id: "overview", label: "Overview",   icon: "◈" },
    { id: "pending",  label: "Approvals",  icon: "⏳", badge: stats.pending },
    { id: "users",    label: "Users",      icon: "◉" },
    { id: "session",  label: "Session",    icon: "◎" },
  ];

  return (
    <div className="ap-root">
      {/* Sidebar */}
      <aside className="ap-sidebar">
        <div className="ap-logo">
          <OneOmicsLogo height={36} maxWidth="190px" dark={false} />
        </div>
        <nav className="ap-nav">
          {tabs.map(t => (
            <button key={t.id} className={`ap-nav-item ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}>
              <span className="nav-icon">{t.icon}</span>
              {t.label}
              {t.badge > 0 && <span className="nav-badge">{t.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="ap-user-box">
          <div className="ap-avatar">{user?.username?.[0]?.toUpperCase() || "A"}</div>
          <div className="ap-user-info">
            <div className="ap-user-name">{user?.username || "Admin"}</div>
            <div className="ap-user-role">{user?.role || "admin"}</div>
          </div>
          <button className="ap-logout" onClick={handleLogout} title="Logout">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="ap-main">
        <div className="ap-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {onBack && (
              <button onClick={onBack} style={{
                background: 'none', border: '1px solid #e2e8f0',
                borderRadius: 6, color: '#64748b', cursor: 'pointer',
                padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: "'Barlow', sans-serif", fontSize: 12,
                letterSpacing: '0.03em', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#003087'; e.currentTarget.style.color = '#003087'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M10 3L5 8l5 5"/>
                </svg>
                Dashboard
              </button>
            )}
            <div>
              <h1 className="ap-page-title">{tabs.find(t => t.id === activeTab)?.label}</h1>
              <p className="ap-page-sub">QC Report Administration</p>
            </div>
          </div>
          <div className="ap-time">
            {new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        </div>

        {feedback && <div className={`ap-feedback ${feedback.type}`}>{feedback.msg}</div>}

        {/* OVERVIEW */}
        {activeTab === "overview" && (
          <div className="ap-content">
            <div className="stat-grid">
              {[
                { label: "Total Reports", value: stats.reports,  icon: "📋", color: "#00b4a0" },
                { label: "Uploads",       value: stats.uploads,  icon: "📁", color: "#3b82f6" },
                { label: "Total Users",   value: stats.users,    icon: "👤", color: "#a78bfa" },
                { label: "Pending",       value: stats.pending,  icon: "⏳", color: "#f59e0b",
                  onClick: () => setActiveTab("pending") },
              ].map(s => (
                <div className="stat-card" key={s.label}
                  style={{ "--accent": s.color, cursor: s.onClick ? "pointer" : "default" }}
                  onClick={s.onClick}>
                  <span className="stat-icon">{s.icon}</span>
                  <div className="stat-value">{s.value ?? "—"}</div>
                  <div className="stat-label">{s.label}</div>
                  {s.onClick && s.value > 0 && (
                    <div style={{ fontSize: 10, color: s.color, marginTop: 4 }}>Click to review →</div>
                  )}
                </div>
              ))}
            </div>
            <div className="info-card">
              <h3 className="card-title">System Status</h3>
              <div className="status-rows">
                <div className="status-row"><span>Backend API</span><span className="status-ok">● Online</span></div>
                <div className="status-row"><span>Auth Service</span><span className="status-ok">● Active</span></div>
                <div className="status-row"><span>File Storage</span><span className="status-ok">● Ready</span></div>
              </div>
            </div>
          </div>
        )}

        {/* PENDING APPROVALS */}
        {activeTab === "pending" && (
          <div className="ap-content">
            <div className="info-card">
              <h3 className="card-title">Pending Registration Requests</h3>
              {loadingUsers ? (
                <p className="ap-muted">Loading...</p>
              ) : pendingUsers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                  <p className="ap-muted">No pending requests.</p>
                </div>
              ) : (
                <div className="pending-list">
                  {pendingUsers.map(u => (
                    <div key={u._id} className="pending-card">
                      <div className="pending-avatar">{u.username?.[0]?.toUpperCase()}</div>
                      <div className="pending-info">
                        <div className="pending-name">{u.username}</div>
                        <div className="pending-meta">
                          {u.email && <span>✉ {u.email}</span>}
                          {u.phone && <span>📞 {u.phone}</span>}
                          {u.institute && <span>🏢 {u.institute}</span>}
                          {u.city && <span>📍 {u.city}</span>}
                        </div>
                        <div className="pending-date">
                          Requested: {u.createdAt ? new Date(u.createdAt).toLocaleString("en-IN") : "—"}
                        </div>
                      </div>
                      <div className="pending-actions">
                        <button className="approve-btn" onClick={() => handleApprove(u._id)}>✓ Approve</button>
                        <button className="reject-btn"  onClick={() => handleReject(u._id)}>✕ Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* USERS */}
        {activeTab === "users" && (
          <div className="ap-content">
            <div className="info-card">
              <h3 className="card-title">Create New User</h3>
              <form className="create-user-form" onSubmit={handleCreateUser}>
                <input className="ap-input" placeholder="Username"
                  value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} />
                <input className="ap-input" type="password" placeholder="Password"
                  value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                <select className="ap-input" value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
                <button className="ap-btn-primary" type="submit">Create User</button>
              </form>
            </div>
            <div className="info-card">
              <h3 className="card-title">Approved Users</h3>
              {loadingUsers ? <p className="ap-muted">Loading...</p>
                : users.length === 0 ? <p className="ap-muted">No approved users found.</p>
                : (
                  <table className="user-table">
                    <thead><tr><th>Username</th><th>Email</th><th>Institute</th><th>Role</th><th>Created</th><th></th></tr></thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u._id || u.username}>
                          <td>{u.username}</td>
                          <td style={{ fontSize: 12, color: "#64748b" }}>{u.email || "—"}</td>
                          <td style={{ fontSize: 12, color: "#64748b" }}>{u.institute || "—"}</td>
                          <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                          <td>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</td>
                          <td>
                            {u.username !== user?.username && (
                              <button className="del-btn" onClick={() => handleDeleteUser(u._id)}>Delete</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        )}

        {/* SESSION */}
        {activeTab === "session" && (
          <div className="ap-content">
            <div className="info-card">
              <h3 className="card-title">Current Session</h3>
              <div className="session-rows">
                <div className="srow"><span>Logged in as</span><strong>{user?.username}</strong></div>
                <div className="srow"><span>Role</span><strong>{user?.role}</strong></div>
                <div className="srow"><span>Session start</span><strong>{new Date().toLocaleTimeString()}</strong></div>
              </div>
              <button className="ap-btn-danger" onClick={handleLogout} style={{ marginTop: 20 }}>Sign Out</button>
            </div>
          </div>
        )}
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        .ap-root{display:flex;min-height:100vh;background:#f4f7fb;color:#1a2a3a;font-family:'Barlow', sans-serif}
        .ap-sidebar{width:220px;flex-shrink:0;background:#ffffff;border-right:1px solid rgba(0,0,0,0.08);display:flex;flex-direction:column;padding:24px 0;box-shadow:2px 0 10px rgba(0,0,0,0.05)}
        .ap-logo{display:flex;align-items:center;gap:12px;padding:0 20px 24px;border-bottom:1px solid rgba(0,0,0,0.07);margin-bottom:20px}
        .ap-logo-icon{width:38px;height:38px;border-radius:10px;background:#003087;color:#00b4d8;font-family:'Barlow', sans-serif;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center}
        .ap-logo-title{font-size:13px;font-weight:700;color:#1a2a3a}
        .ap-logo-sub{font-size:11px;color:#a0b4c8}
        .ap-nav{display:flex;flex-direction:column;gap:4px;padding:0 12px;flex:1}
        .ap-nav-item{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:8px;background:none;border:none;color:#5a7a9a;font-family:'Barlow', sans-serif;font-size:13px;cursor:pointer;text-align:left;transition:all 0.15s;position:relative}
        .ap-nav-item:hover{background:rgba(0,0,0,0.04);color:#1a2a3a}
        .ap-nav-item.active{background:rgba(0,48,135,0.08);color:#003087;font-weight:600}
        .nav-icon{font-size:15px}
        .nav-badge{margin-left:auto;background:#f59e0b;color:#fff;font-size:10px;font-weight:700;padding:2px 7px;border-radius:999px;font-family:'Barlow', sans-serif}
        .ap-user-box{display:flex;align-items:center;gap:10px;padding:16px 16px 0;border-top:1px solid rgba(0,0,0,0.07);margin-top:16px}
        .ap-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,#003087,#00b4d8);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff}
        .ap-user-name{font-size:13px;font-weight:600;color:#1a2a3a}
        .ap-user-role{font-size:11px;color:#a0b4c8;text-transform:capitalize}
        .ap-user-info{flex:1}
        .ap-logout{background:none;border:none;color:#a0b4c8;cursor:pointer;padding:4px;transition:color 0.2s}
        .ap-logout:hover{color:#ef4444}
        .ap-main{flex:1;display:flex;flex-direction:column;overflow:hidden}
        .ap-topbar{display:flex;align-items:flex-start;justify-content:space-between;padding:28px 32px 20px;border-bottom:1px solid rgba(0,0,0,0.07);background:#fff}
        .ap-page-title{font-size:22px;font-weight:700;color:#1a2a3a}
        .ap-page-sub{font-size:13px;color:#a0b4c8;margin-top:3px}
        .ap-time{font-family:'Barlow', sans-serif;font-size:11px;color:#a0b4c8}
        .ap-feedback{margin:16px 32px 0;padding:12px 16px;border-radius:8px;font-size:13px;animation:fadeIn 0.3s ease}
        .ap-feedback.success{background:rgba(5,150,105,0.08);border:1px solid rgba(5,150,105,0.25);color:#065f46}
        .ap-feedback.error{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.2);color:#dc2626}
        @keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1}}
        .ap-content{padding:28px 32px;display:flex;flex-direction:column;gap:20px;overflow-y:auto}
        .stat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
        .stat-card{background:#fff;border:1px solid rgba(0,0,0,0.07);border-radius:14px;padding:20px;display:flex;flex-direction:column;gap:8px;border-top:3px solid var(--accent);transition:all 0.15s;box-shadow:0 1px 6px rgba(0,0,0,0.05)}
        .stat-card:hover{box-shadow:0 4px 14px rgba(0,0,0,0.1);transform:translateY(-1px)}
        .stat-icon{font-size:22px}
        .stat-value{font-size:28px;font-weight:700;color:#1a2a3a}
        .stat-label{font-size:12px;color:#a0b4c8}
        .info-card{background:#fff;border:1px solid rgba(0,0,0,0.07);border-radius:14px;padding:22px;box-shadow:0 1px 6px rgba(0,0,0,0.04)}
        .card-title{font-size:14px;font-weight:700;color:#1a2a3a;margin-bottom:16px}
        .status-rows,.session-rows{display:flex;flex-direction:column;gap:10px}
        .status-row,.srow{display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#5a7a9a;padding:8px 0;border-bottom:1px solid rgba(0,0,0,0.06)}
        .status-ok{color:#059669;font-size:12px;font-weight:600}
        .srow strong{color:#1a2a3a}
        .pending-list{display:flex;flex-direction:column;gap:12px}
        .pending-card{display:flex;align-items:center;gap:14px;padding:16px;background:#f8fafc;border:1px solid rgba(0,0,0,0.07);border-radius:12px;transition:all 0.15s}
        .pending-card:hover{background:#f0f4f9;box-shadow:0 2px 10px rgba(0,0,0,0.07)}
        .pending-avatar{width:44px;height:44px;border-radius:50%;background:linear-gradient(135deg,#f59e0b,#ef4444);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700;color:#fff;flex-shrink:0}
        .pending-info{flex:1;min-width:0}
        .pending-name{font-size:15px;font-weight:700;color:#1a2a3a;margin-bottom:4px}
        .pending-meta{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:4px}
        .pending-meta span{font-size:11px;color:#5a7a9a;font-family:'Barlow', sans-serif}
        .pending-date{font-size:11px;color:#a0b4c8;font-family:'Barlow', sans-serif}
        .pending-actions{display:flex;gap:8px;flex-shrink:0}
        .approve-btn{background:rgba(5,150,105,0.1);color:#059669;border:1px solid rgba(5,150,105,0.3);border-radius:8px;padding:8px 16px;font-family:'Barlow', sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;letter-spacing:0.03em}
        .approve-btn:hover{background:rgba(5,150,105,0.18);border-color:#059669}
        .reject-btn{background:rgba(239,68,68,0.07);color:#dc2626;border:1px solid rgba(239,68,68,0.25);border-radius:8px;padding:8px 16px;font-family:'Barlow', sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;letter-spacing:0.03em}
        .reject-btn:hover{background:rgba(239,68,68,0.14)}
        .create-user-form{display:flex;gap:10px;flex-wrap:wrap}
        .ap-input{background:#f8fafc;border:1px solid rgba(0,0,0,0.12);border-radius:8px;padding:10px 12px;color:#1a2a3a;font-family:'Barlow', sans-serif;font-size:13px;outline:none;flex:1;min-width:130px}
        .ap-input:focus{border-color:#003087;box-shadow:0 0 0 3px rgba(0,48,135,0.08)}
        .ap-btn-primary{background:#003087;color:#fff;border:none;border-radius:8px;padding:10px 18px;font-family:'Barlow', sans-serif;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.15s;white-space:nowrap}
        .ap-btn-primary:hover{background:#002070}
        .ap-btn-danger{background:rgba(239,68,68,0.07);color:#dc2626;border:1px solid rgba(239,68,68,0.2);border-radius:8px;padding:10px 18px;font-family:'Barlow', sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.15s}
        .ap-btn-danger:hover{background:rgba(239,68,68,0.14)}
        .user-table{width:100%;border-collapse:collapse;font-size:13px}
        .user-table th{text-align:left;padding:8px 12px;font-family:'Barlow', sans-serif;font-size:11px;font-weight:500;letter-spacing:0.06em;color:#a0b4c8;border-bottom:2px solid rgba(0,0,0,0.07);text-transform:uppercase}
        .user-table td{padding:10px 12px;border-bottom:1px solid rgba(0,0,0,0.05);color:#1a2a3a}
        .user-table tr:hover td{background:#f8fafc}
        .role-badge{font-family:'Barlow', sans-serif;font-size:10px;font-weight:700;padding:3px 8px;border-radius:999px;text-transform:uppercase}
        .role-badge.admin{background:rgba(0,48,135,0.08);color:#003087;border:1px solid rgba(0,48,135,0.2)}
        .role-badge.viewer{background:rgba(0,180,216,0.08);color:#0369a1;border:1px solid rgba(0,180,216,0.25)}
        .del-btn{background:none;border:1px solid rgba(239,68,68,0.25);color:#dc2626;border-radius:6px;padding:4px 10px;font-size:11px;cursor:pointer;transition:all 0.15s}
        .del-btn:hover{background:rgba(239,68,68,0.07)}
        .ap-muted{color:#a0b4c8;font-size:13px}
      `}</style>
    </div>
  );
}